import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { MaterialReactTable } from "material-react-table";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Paper,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Badge,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tab,
  Tabs,
  Avatar,
  LinearProgress,
  Fab,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  useTheme,
} from "@mui/material";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Download,
  FilterList,
  PersonAdd,
  Assessment,
  Event,
  Group,
  Person,
  TrendingUp,
  TrendingDown,
  Schedule,
  Clear,
  FileDownload,
  Print,
  Refresh,
  Settings,
  Dashboard,
  Login,
  Logout,
  AccessTime,
  LocationOn,
  CheckCircle,
  Cancel,
  Edit,
  Delete,
  Add,
  PlayArrow,
  Stop,
  WorkOff,
  Sick,
  BeachAccess,
  Home,
  Business,
  Computer,
  Visibility,
  GetApp,
} from "@mui/icons-material";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isoWeek from "dayjs/plugin/isoWeek";
import weekOfYear from "dayjs/plugin/weekOfYear";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { getToken, base_url } from "../../config/config";
import WorkingCheckIn from "./WorkingCheckIn";
import Header from "../../components/Header";
import { tokens } from "../../theme";

dayjs.extend(duration);
dayjs.extend(customParseFormat);
dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);

const AdvancedManagementLog = () => {
  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // State Management
  const [data, setData] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminsLoading, setAdminsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Load data on component mount
  useEffect(() => {
    fetchData();
    fetchAdmins();
    fetchStatistics();
  }, []);

  // Dialogs
  const [openModal, setOpenModal] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);

  // Admin only - no check-in functionality

  // Form Data
  const [formData, setFormData] = useState({
    user: "",
    date: "",
    checkInTime: "",
    checkOutTime: "",
    workingStatus: "present",
    absenceType: "",
    notes: "",
    location: "office",
  });

  // Advanced Filters
  const [filters, setFilters] = useState({
    admins: [],
    dateRange: [null, null],
    status: "all",
    quickFilter: "",
    location: "all",
  });

  // Export Options
  const [exportOptions, setExportOptions] = useState({
    format: "excel",
    includeStatistics: true,
    groupByAdmin: false,
    includeSummary: true,
  });

  // Statistics
  const [statistics, setStatistics] = useState({
    totalRecords: 0,
    totalAdmins: 0,
    presentToday: 0,
    workingNow: 0,
    totalOvertime: 0,
    totalUndertime: 0,
    attendanceRate: 0,
  });

  // Work Status Colors and Icons
  const getStatusConfig = (status) => {
    const configs = {
      present: { color: "success", icon: <CheckCircle />, label: "Anwesend" },
      working: { color: "info", icon: <PlayArrow />, label: "Arbeitet" },
      completed: { color: "primary", icon: <Stop />, label: "Abgeschlossen" },
      absent: { color: "error", icon: <Cancel />, label: "Abwesend" },
      sick: { color: "warning", icon: <Sick />, label: "Krank" },
      vacation: { color: "secondary", icon: <BeachAccess />, label: "Urlaub" },
      unpaid_leave: {
        color: "default",
        icon: <WorkOff />,
        label: "Unbezahlter Urlaub",
      },
    };
    return configs[status] || configs.absent;
  };

  const getLocationIcon = (location) => {
    const icons = {
      office: <Business />,
      remote: <Home />,
      client_site: <LocationOn />,
      other: <Computer />,
    };
    return icons[location] || <Business />;
  };

  // Helper Functions
  const getAdminName = (admin) => {
    if (typeof admin === "string") return admin;
    return admin.firstName + "" + admin.lastName || "";
  };

  const getAdminList = () => {
    if (!Array.isArray(admins)) {
      return [];
    }
    return admins.map((admin) => getAdminName(admin));
  };

  const formatDuration = (hours) => {
    if (typeof hours !== "number" || hours <= 0) return "0h 0m";
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const formatTime = (timeString) => {
    if (!timeString) return "-";
    return timeString;
  };

  // API Calls removed from here - now in main useEffect

  const fetchAdmins = async () => {
    try {
      setAdminsLoading(true);
      const token = getToken();
      // Fetch ONLY admins for management log
      const response = await axios.get(`${base_url}/v1/GetAllAdmins`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // Ensure we always set an array for admins
      const adminData = response.data?.message?.users || [];
      setAdmins(Array.isArray(adminData) ? adminData : []);
      setError(null);
      console.log("Fetched admins:", adminData); // Debug log to verify we're getting admin data
    } catch (error) {
      console.error("Error fetching admins:", error);
      setAdmins([]); // Reset to empty array on error
      if (error.response?.status === 401) {
        navigate("/signin");
        return;
      }
      showSnackbar("Fehler beim Laden der Admins", "error");
      setError(error);
    } finally {
      setAdminsLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const params = new URLSearchParams();

      if (filters.dateRange[0] && filters.dateRange[1]) {
        params.append("startDate", filters.dateRange[0].format("YYYY-MM-DD"));
        params.append("endDate", filters.dateRange[1].format("YYYY-MM-DD"));
      }

      if (filters.admins.length > 0) {
        params.append("user", filters.admins.join(","));
      }

      if (filters.status !== "all") {
        params.append("status", filters.status);
      }

      const response = await axios.get(
        `${base_url}/v1/management-log/logs?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setData(response.data.data || []);
      setError(null);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/signin");
        return;
      }
      setError(err);
      showSnackbar("Error loading data", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const token = getToken();
      const params = new URLSearchParams();
      if (filters.dateRange[0] && filters.dateRange[1]) {
        params.append("startDate", filters.dateRange[0].format("YYYY-MM-DD"));
        params.append("endDate", filters.dateRange[1].format("YYYY-MM-DD"));
      }

      const response = await axios.get(
        `${base_url}/v1/management-log/statistics?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (response.data.data && response.data.data.length > 0) {
        const stats = response.data.data[0];
        setStatistics({
          totalRecords: stats.totalDays || 0,
          totalAdmins: response.data.data.length,
          presentToday: stats.presentDays || 0,
          workingNow: 0, // This would need real-time data
          totalOvertime: stats.totalOvertime || 0,
          totalUndertime: stats.totalUndertime || 0,
          attendanceRate: stats.attendanceRate || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching statistics:", error);
    }
  };

  // Admin functions only - no check-in/check-out for other users

  // CRUD Operations
  const handleEditClick = (record) => {
    setCurrentRecord(record);
    setFormData({
      user: record.user,
      date: record.date,
      checkInTime: record.checkInTime || "",
      checkOutTime: record.checkOutTime || "",
      workingStatus: record.workingStatus,
      absenceType: record.absenceType || "",
      notes: record.notes || "",
      location: record.location || "office",
    });
    setOpenModal(true);
  };

  const handleAddClick = () => {
    setCurrentRecord(null);
    setFormData({
      user: loggedInUserName,
      date: dayjs().format("YYYY-MM-DD"),
      checkInTime: "",
      checkOutTime: "",
      workingStatus: "present",
      absenceType: "",
      notes: "",
      location: "office",
    });
    setOpenAddModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setOpenAddModal(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = getToken();
      const headers = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      if (currentRecord) {
        await axios.put(
          `${base_url}/v1/management-log/entry/${currentRecord._id}`,
          { ...formData, modifiedBy: "Admin" },
          headers,
        );
        showSnackbar("Eintrag erfolgreich aktualisiert");
      } else {
        await axios.post(
          `${base_url}/v1/management-log/manual-entry`,
          { ...formData, approvedBy: "Admin" },
          headers,
        );
        showSnackbar("Eintrag erfolgreich hinzugefügt");
      }
      handleCloseModal();
      fetchData();
      fetchStatistics();
    } catch (error) {
      console.error("Error saving entry:", error);
      showSnackbar(
        error.response?.data?.message || "Fehler beim Speichern",
        "error",
      );
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (window.confirm("Möchten Sie diesen Eintrag wirklich löschen?")) {
      try {
        const token = getToken();
        await axios.delete(`${base_url}/v1/management-log/entry/${recordId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        showSnackbar("Eintrag erfolgreich gelöscht");
        fetchData();
        fetchStatistics();
      } catch (error) {
        console.error("Error deleting entry:", error);
        showSnackbar("Fehler beim Löschen", "error");
      }
    }
  };

  // Quick Date Filters
  const applyQuickFilter = (filterType) => {
    const today = dayjs();
    let startDate, endDate;

    switch (filterType) {
      case "today":
        startDate = today.startOf("day");
        endDate = today.endOf("day");
        break;
      case "week":
        startDate = today.startOf("isoWeek");
        endDate = today.endOf("isoWeek");
        break;
      case "month":
        startDate = today.startOf("month");
        endDate = today.endOf("month");
        break;
      case "lastMonth":
        startDate = today.subtract(1, "month").startOf("month");
        endDate = today.subtract(1, "month").endOf("month");
        break;
      default:
        startDate = null;
        endDate = null;
    }

    setFilters({
      ...filters,
      dateRange: [startDate, endDate],
      quickFilter: filterType,
    });
  };

  // Export Functions
  const exportData = async () => {
    try {
      const token = getToken();
      const params = new URLSearchParams({ format: exportOptions.format });

      if (filters.dateRange[0] && filters.dateRange[1]) {
        params.append("startDate", filters.dateRange[0].format("YYYY-MM-DD"));
        params.append("endDate", filters.dateRange[1].format("YYYY-MM-DD"));
      }

      // Always expect a binary blob
      const response = await axios.get(
        `${base_url}/v1/management-log/export?${params.toString()}`,
        {
          responseType: "blob",
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // Determine MIME & extension
      const isExcel = exportOptions.format === "excel";
      const mime = isExcel
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "application/pdf";
      const ext = isExcel ? "xlsx" : "pdf";

      const blob = new Blob([response.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `management-log-${dayjs().format("YYYY-MM-DD")}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export error:", error);
      showSnackbar("Export fehlgeschlagen", "error");
    }
  };

  // Snackbar
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Filter Data
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      if (filters.admins.length > 0 && !filters.admins.includes(item.user)) {
        return false;
      }

      if (filters.dateRange[0] && filters.dateRange[1]) {
        const itemDate = dayjs(item.date);
        if (
          !itemDate.isBetween(
            filters.dateRange[0],
            filters.dateRange[1],
            null,
            "[]",
          )
        ) {
          return false;
        }
      }

      if (filters.status !== "all" && item.workingStatus !== filters.status) {
        return false;
      }

      if (filters.location !== "all" && item.location !== filters.location) {
        return false;
      }

      return true;
    });
  }, [data, filters]);

  // Components
  const StatCard = ({ title, value, icon, color, trend }) => (
    <Card
      sx={{
        height: "100%",
        position: "relative",
        overflow: "visible",
        backgroundColor: colors.primary[400],
        border: `1px solid ${colors.primary[500]}`,
      }}
    >
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography color={colors.grey[300]} gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h5" component="div" sx={{ color }}>
              {value}
            </Typography>
            {trend !== undefined && (
              <Box display="flex" alignItems="center" mt={1}>
                {trend > 0 ? (
                  <TrendingUp sx={{ color: "success.main", fontSize: 16 }} />
                ) : trend < 0 ? (
                  <TrendingDown sx={{ color: "error.main", fontSize: 16 }} />
                ) : null}
                <Typography variant="caption" sx={{ ml: 0.5 }}>
                  {trend !== 0 ? `${Math.abs(trend)}%` : "Unverändert"}
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ color, opacity: 0.3 }}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  );

  const FilterPanel = () => (
    <Paper
      sx={{
        p: 3,
        mb: 3,
        backgroundColor: colors.primary[400],
        borderRadius: "10px",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6" color={colors.grey[100]}>
          <FilterList sx={{ verticalAlign: "middle", mr: 1 }} />
          Filter
        </Typography>
        <Button
          size="small"
          onClick={() => {
            setFilters({
              admins: [],
              dateRange: [null, null],
              status: "all",
              quickFilter: "",
              location: "all",
            });
          }}
          startIcon={<Clear />}
          sx={{ color: "#2563eb" }}
        >
          Filter zurücksetzen
        </Button>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Autocomplete
            multiple
            value={filters.admins}
            onChange={(e, value) => setFilters({ ...filters, admins: value })}
            options={getAdminList()}
            renderInput={(params) => <TextField {...params} label="Admins" />}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <ToggleButtonGroup
            value={filters.quickFilter}
            exclusive
            onChange={(e, value) => value && applyQuickFilter(value)}
            fullWidth
            size="small"
          >
            <ToggleButton value="today">Heute</ToggleButton>
            <ToggleButton value="week">Woche</ToggleButton>
            <ToggleButton value="month">Monat</ToggleButton>
          </ToggleButtonGroup>
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value })
              }
              label="Status"
            >
              <MenuItem value="all">Alle</MenuItem>
              <MenuItem value="present">Anwesend</MenuItem>
              <MenuItem value="working">Arbeitet</MenuItem>
              <MenuItem value="completed">Abgeschlossen</MenuItem>
              <MenuItem value="absent">Abwesend</MenuItem>
              <MenuItem value="sick">Krank</MenuItem>
              <MenuItem value="vacation">Urlaub</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Standort</InputLabel>
            <Select
              value={filters.location}
              onChange={(e) =>
                setFilters({ ...filters, location: e.target.value })
              }
              label="Standort"
            >
              <MenuItem value="all">Alle</MenuItem>
              <MenuItem value="office">Büro</MenuItem>
              <MenuItem value="remote">Home Office</MenuItem>
              <MenuItem value="client_site">Kundenstandort</MenuItem>
              <MenuItem value="other">Sonstiges</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" gap={2}>
              <DatePicker
                label="Von"
                value={filters.dateRange[0]}
                onChange={(newValue) =>
                  setFilters({
                    ...filters,
                    dateRange: [newValue, filters.dateRange[1]],
                    quickFilter: "",
                  })
                }
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
              <DatePicker
                label="Bis"
                value={filters.dateRange[1]}
                onChange={(newValue) =>
                  setFilters({
                    ...filters,
                    dateRange: [filters.dateRange[0], newValue],
                    quickFilter: "",
                  })
                }
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Box>
          </LocalizationProvider>
        </Grid>
      </Grid>
    </Paper>
  );

  // const InfoPanel = () => (
  //   <Paper
  //     sx={{
  //       p: 3,
  //       mb: 3,
  //       backgroundColor: colors.primary[400],
  //       color: colors.grey[100],
  //       border: `1px solid ${colors.blueAccent[700]}`,
  //       borderRadius: "10px",
  //     }}
  //   >
  //     <Typography variant="h6" gutterBottom color="inherit">
  //       <Assessment sx={{ verticalAlign: "middle", mr: 1 }} />
  //       Anwesenheitsverwaltung Dashboard
  //     </Typography>
  //     <Typography variant="body2" sx={{ opacity: 0.9 }} color="inherit">
  //       Hier können Sie Anwesenheitsdaten verwalten, Berichte erstellen und
  //       Einträge bearbeiten. Für Check-in/Check-out verwenden Sie die
  //       Self-Service Seite.
  //     </Typography>
  //     <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
  //       <Button
  //         variant="outlined"
  //         sx={{
  //           color: colors.grey[100],
  //           borderColor: colors.blueAccent[500],
  //           "&:hover": {
  //             borderColor: colors.blueAccent[700],
  //             backgroundColor: colors.blueAccent[700],
  //           },
  //         }}
  //         onClick={() => navigate("/self-service-checkin")}
  //       >
  //         Self-Service Check-in
  //       </Button>
  //       <Button
  //         variant="outlined"
  //         sx={{
  //           color: colors.grey[100],
  //           borderColor: colors.blueAccent[500],
  //           "&:hover": {
  //             borderColor: colors.blueAccent[700],
  //             backgroundColor: colors.blueAccent[700],
  //           },
  //         }}
  //         onClick={() => navigate("/checkin-checkout")}
  //       >
  //         Legacy Check-in
  //       </Button>
  //     </Box>
  //   </Paper>
  // );

  // Get full name for the logged-in user
  const loggedInUserName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : "";

  if (loading || adminsLoading) {
    return (
      <Box m="20px">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box m="20px">
        <Alert severity="error" sx={{ mb: 2 }}>
          Fehler beim Laden der Daten. Bitte versuchen Sie es später erneut.
        </Alert>
        <Button
          variant="contained"
          onClick={() => {
            setError(null);
            fetchData();
            fetchAdmins();
          }}
          startIcon={<Refresh />}
          sx={{
            backgroundColor: "#2563eb",
            "&:hover": { backgroundColor: "#1d4ed8" },
          }}
        >
          Erneut versuchen
        </Button>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Header
        title="ERWEITERTE ANWESENHEITSVERWALTUNG"
        subtitle="Verwalten, filtern und exportieren Sie Admin-Anwesenheiten"
      />
      <Box display="flex" justifyContent="flex-end" mb={3} gap={1}>
        <Tooltip title="Daten aktualisieren">
          <IconButton
            onClick={() => {
              fetchData();
              fetchAdmins();
              fetchStatistics();
            }}
            sx={{
              border: `1px solid ${colors.blueAccent[500]}`,
              color: colors.grey[100],
            }}
          >
            <Refresh />
          </IconButton>
        </Tooltip>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={handleAddClick}
          sx={{
            backgroundColor: "#2563eb",
            "&:hover": { backgroundColor: "#1d4ed8" },
          }}
        >
          Manueller Eintrag
        </Button>
        <Button
          variant="contained"
          startIcon={<FileDownload />}
          onClick={() => setExportDialogOpen(true)}
          sx={{
            backgroundColor: "#2563eb",
            "&:hover": { backgroundColor: "#1d4ed8" },
          }}
        >
          Exportieren
        </Button>
      </Box>

      {/* Info Panel */}
      {/* <InfoPanel /> */}

      {/* Tabs */}
      <Tabs
        value={selectedTab}
        onChange={(e, v) => setSelectedTab(v)}
        sx={{
          mb: 3,
          "& .MuiTab-root": { color: colors.grey[100] },
          "& .Mui-selected": { color: "#2563eb" },
          "& .MuiTabs-indicator": { backgroundColor: "#2563eb" },
        }}
      >
        <Tab label="Mein Check-in" icon={<Login />} iconPosition="start" />
        <Tab label="Dashboard" icon={<Dashboard />} iconPosition="start" />
        <Tab label="Detailansicht" icon={<Assessment />} iconPosition="start" />
        <Tab label="Statistiken" icon={<Group />} iconPosition="start" />
      </Tabs>

      {/* Tab Content */}
      {selectedTab === 0 && (
        <Box sx={{ mt: 3 }}>
          <WorkingCheckIn />
        </Box>
      )}

      {selectedTab === 1 && (
        <>
          {/* Statistics Cards */}
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Anwesende heute"
                value={statistics.presentToday}
                icon={<CheckCircle sx={{ fontSize: 40 }} />}
                color="success.main"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Arbeiten derzeit"
                value={statistics.workingNow}
                icon={<PlayArrow sx={{ fontSize: 40 }} />}
                color="info.main"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Überstunden gesamt"
                value={formatDuration(statistics.totalOvertime)}
                icon={<TrendingUp sx={{ fontSize: 40 }} />}
                color="success.main"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Anwesenheitsrate"
                value={`${statistics.attendanceRate.toFixed(1)}%`}
                icon={<Assessment sx={{ fontSize: 40 }} />}
                color="primary.main"
              />
            </Grid>
          </Grid>

          <FilterPanel />

          <MaterialReactTable
            columns={[
              {
                accessorKey: "user",
                header: "Admin",
                Cell: ({ cell }) => (
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ mr: 1, bgcolor: "primary.main" }}>
                      {cell.getValue().charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography variant="body2" fontWeight="medium">
                      {cell.getValue()}
                    </Typography>
                  </Box>
                ),
              },
              {
                accessorKey: "date",
                header: "Datum",
                Cell: ({ cell }) => {
                  const date = dayjs(cell.getValue());
                  return (
                    <Box>
                      <Typography variant="body2">
                        {date.format("DD.MM.YYYY")}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {date.format("dddd")}
                      </Typography>
                    </Box>
                  );
                },
              },
              {
                accessorKey: "checkInTime",
                header: "Check-in",
                Cell: ({ cell }) => (
                  <Chip
                    icon={<Login />}
                    label={formatTime(cell.getValue())}
                    size="small"
                    color={cell.getValue() ? "success" : "default"}
                    variant={cell.getValue() ? "filled" : "outlined"}
                  />
                ),
              },
              {
                accessorKey: "checkOutTime",
                header: "Check-out",
                Cell: ({ cell }) => (
                  <Chip
                    icon={<Logout />}
                    label={formatTime(cell.getValue())}
                    size="small"
                    color={cell.getValue() ? "error" : "default"}
                    variant={cell.getValue() ? "filled" : "outlined"}
                  />
                ),
              },
              {
                accessorKey: "totalWorkingHours",
                header: "Arbeitszeit",
                Cell: ({ cell }) => (
                  <Typography fontWeight="medium">
                    {formatDuration(cell.getValue())}
                  </Typography>
                ),
              },
              {
                accessorKey: "workingStatus",
                header: "Status",
                Cell: ({ cell }) => {
                  const config = getStatusConfig(cell.getValue());
                  return (
                    <Chip
                      icon={config.icon}
                      label={config.label}
                      color={config.color}
                      size="small"
                    />
                  );
                },
              },
              {
                accessorKey: "location",
                header: "Standort",
                Cell: ({ cell }) => (
                  <Chip
                    icon={getLocationIcon(cell.getValue())}
                    label={
                      cell.getValue() === "office"
                        ? "Büro"
                        : cell.getValue() === "remote"
                          ? "Home Office"
                          : cell.getValue() === "client_site"
                            ? "Kunde"
                            : "Sonstiges"
                    }
                    size="small"
                    variant="outlined"
                  />
                ),
              },
              {
                accessorKey: "overtime",
                header: "Überstunden",
                Cell: ({ row }) => {
                  const overtime = row.original.overtime?.hours || 0;
                  return overtime > 0 ? (
                    <Chip
                      label={formatDuration(overtime)}
                      color="success"
                      size="small"
                    />
                  ) : (
                    "-"
                  );
                },
              },
              {
                accessorKey: "actions",
                header: "Aktionen",
                Cell: ({ row }) => (
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => handleEditClick(row.original)}
                      color="primary"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteRecord(row.original._id)}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                ),
              },
            ]}
            data={filteredData}
            initialState={{
              pagination: { pageIndex: 0, pageSize: 10 },
              sorting: [{ id: "date", desc: true }],
            }}
            enablePagination
            enableSorting
            enableColumnFilter
            enableRowSelection
          />
        </>
      )}

      {selectedTab === 2 && (
        <>
          <FilterPanel />
          <MaterialReactTable
            columns={[
              { accessorKey: "user", header: "Mitarbeiter" },
              {
                accessorKey: "date",
                header: "Datum",
                Cell: ({ cell }) => dayjs(cell.getValue()).format("DD.MM.YYYY"),
              },
              { accessorKey: "checkInTime", header: "Check-in" },
              { accessorKey: "checkOutTime", header: "Check-out" },
              {
                accessorKey: "totalWorkingHours",
                header: "Arbeitszeit",
                Cell: ({ cell }) => formatDuration(cell.getValue()),
              },
              { accessorKey: "workingStatus", header: "Status" },
              { accessorKey: "location", header: "Standort" },
              { accessorKey: "notes", header: "Notizen" },
              {
                accessorKey: "actions",
                header: "Aktionen",
                Cell: ({ row }) => (
                  <Button
                    size="small"
                    onClick={() => handleEditClick(row.original)}
                  >
                    Bearbeiten
                  </Button>
                ),
              },
            ]}
            data={filteredData}
            enableFullScreenToggle
            enableGlobalFilter
            enableColumnResizing
          />
        </>
      )}

      {selectedTab === 3 && (
        <>
          <FilterPanel />
          <Grid container spacing={3}>
            {/* Statistics overview would go here */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Detaillierte Statistiken
                  </Typography>
                  <Typography>
                    Statistiken werden basierend auf den aktuellen Filtern
                    berechnet.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* Add/Edit Manual Entry Modal */}
      <Dialog
        open={openModal || openAddModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {currentRecord ? "Eintrag bearbeiten" : "Manueller Eintrag"}
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Admin"
                  type="text"
                  name="user"
                  value={formData.user}
                  disabled
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Datum"
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Check-in Zeit"
                  type="time"
                  name="checkInTime"
                  value={formData.checkInTime}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Check-out Zeit"
                  type="time"
                  name="checkOutTime"
                  value={formData.checkOutTime}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.workingStatus}
                    onChange={handleChange}
                    name="workingStatus"
                    label="Status"
                  >
                    <MenuItem value="present">Anwesend</MenuItem>
                    <MenuItem value="working">Arbeitet</MenuItem>
                    <MenuItem value="completed">Abgeschlossen</MenuItem>
                    <MenuItem value="absent">Abwesend</MenuItem>
                    <MenuItem value="sick">Krank</MenuItem>
                    <MenuItem value="vacation">Urlaub</MenuItem>
                    <MenuItem value="unpaid_leave">Unbezahlter Urlaub</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Standort</InputLabel>
                  <Select
                    value={formData.location}
                    onChange={handleChange}
                    name="location"
                    label="Standort"
                  >
                    <MenuItem value="office">Büro</MenuItem>
                    <MenuItem value="remote">Home Office</MenuItem>
                    <MenuItem value="client_site">Kundenstandort</MenuItem>
                    <MenuItem value="other">Sonstiges</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {(formData.workingStatus === "sick" ||
                formData.workingStatus === "vacation" ||
                formData.workingStatus === "unpaid_leave") && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Abwesenheitstyp</InputLabel>
                    <Select
                      value={formData.absenceType}
                      onChange={handleChange}
                      name="absenceType"
                      label="Abwesenheitstyp"
                    >
                      <MenuItem value="sick">Krank</MenuItem>
                      <MenuItem value="vacation">Urlaub</MenuItem>
                      <MenuItem value="unpaid_leave">
                        Unbezahlter Urlaub
                      </MenuItem>
                      <MenuItem value="authorized">Genehmigt</MenuItem>
                      <MenuItem value="unauthorized">Ungenehmigt</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notizen"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Abbrechen</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              backgroundColor: "#2563eb",
              "&:hover": { backgroundColor: "#1d4ed8" },
            }}
          >
            {currentRecord ? "Speichern" : "Hinzufügen"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <FileDownload sx={{ mr: 1 }} />
            Daten exportieren
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Alert severity="info">
                Export enthält alle gefilterten Daten mit detaillierten
                Arbeitszeit-Informationen.
              </Alert>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={exportOptions.format === "excel" ? 3 : 1}
                sx={{
                  p: 2,
                  cursor: "pointer",
                  border: "2px solid",
                  borderColor:
                    exportOptions.format === "excel"
                      ? "primary.main"
                      : "divider",
                }}
                onClick={() =>
                  setExportOptions({ ...exportOptions, format: "excel" })
                }
              >
                <Box display="flex" alignItems="center" mb={1}>
                  <GetApp sx={{ mr: 1, color: "success.main" }} />
                  <Typography variant="h6">Excel Export</Typography>
                </Box>
                <Typography variant="body2" color="textSecondary">
                  Detaillierte Tabelle mit allen Anwesenheitsdaten
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={exportOptions.format === "pdf" ? 3 : 1}
                sx={{
                  p: 2,
                  cursor: "pointer",
                  border: "2px solid",
                  borderColor:
                    exportOptions.format === "pdf" ? "primary.main" : "divider",
                }}
                onClick={() =>
                  setExportOptions({ ...exportOptions, format: "pdf" })
                }
              >
                <Box display="flex" alignItems="center" mb={1}>
                  <Print sx={{ mr: 1, color: "error.main" }} />
                  <Typography variant="h6">PDF Export</Typography>
                </Box>
                <Typography variant="body2" color="textSecondary">
                  Formatierter Bericht für Druck und Archivierung
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={exportOptions.includeStatistics}
                    onChange={(e) =>
                      setExportOptions({
                        ...exportOptions,
                        includeStatistics: e.target.checked,
                      })
                    }
                  />
                }
                label="Statistiken einschließen"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={exportData}
            variant="contained"
            startIcon={<Download />}
            sx={{
              backgroundColor: "#2563eb",
              "&:hover": { backgroundColor: "#1d4ed8" },
            }}
          >
            Exportieren
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdvancedManagementLog;
